import React from "react";
import { Link } from "react-router-dom";
import {
  Car,
  Home as HomeIcon,
  Building2,
  Plane,
  Users,
  ShieldCheck,
  Wallet,
  Clock,
  HeartHandshake,
  ArrowRight,
  Phone,
  Star,
  CheckCircle2,
  HardHat,
  RefreshCw,
} from "lucide-react";
import { services, stats, whyUs, testimonials, company, icbcRenewal } from "../mock";
import QuoteForm from "../components/QuoteForm";
import PartnersCarousel from "../components/PartnersCarousel";

const iconMap = {
  Car,
  Home: HomeIcon,
  Building2,
  Plane,
  Users,
  ShieldCheck,
  Wallet,
  Clock,
  HeartHandshake,
  HardHat,
};

const Home = () => {
  return (
    <div className="relative">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="aurora bg-[#8ec4ec] h-96 w-96 -top-24 -left-20" />
        <div className="aurora bg-[#8ec4ec] h-96 w-96 top-10 right-0" />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 glass px-4 py-1.5 rounded-full text-sm font-semibold text-[#003c7a] mb-6">
              <Star size={15} className="fill-[#0066b3] text-[#0066b3]" />
              {company.tagline}
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.08] text-[#0a2b52]">
              Insurance made
              <span className="brand-gradient-text"> simple, modern & </span>
              built around you
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Your trusted independent insurance broker in Surrey, BC. Home,
              auto (ICBC Autoplan), business, travel and Super Visa insurance —
              all under one roof. Let our agents help you save.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="brand-btn px-7 py-3.5 rounded-full font-semibold flex items-center gap-2"
              >
                Get a Free Quote <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${company.phoneRaw}`}
                className="glass-strong px-6 py-3.5 rounded-full font-semibold text-[#0a2b52] flex items-center gap-2 hover:text-[#0066b3] transition-colors"
              >
                <Phone size={18} /> {company.phone}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {["Independent broker", "Open 7 days a week", "Monthly payment plans"].map(
                (t) => (
                  <span key={t} className="flex items-center gap-1.5">
                    <CheckCircle2 size={16} className="text-[#0066b3]" /> {t}
                  </span>
                )
              )}
            </div>
          </div>

          {/* hero visual + quick quote */}
          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="glass-strong rounded-3xl p-2 floaty">
              <img
                src="https://images.unsplash.com/photo-1588979355313-6711a095465f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseXxlbnwwfHx8fDE3ODU0NTE5ODR8MA&ixlib=rb-4.1.0&q=85"
                alt="Happy protected family"
                className="rounded-2xl w-full h-[380px] object-cover"
              />
            </div>
            <div className="glass-strong absolute -bottom-6 -left-6 rounded-2xl px-5 py-4 hidden sm:flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0066b3] to-[#003c7a] flex items-center justify-center text-white">
                <ShieldCheck size={22} />
              </div>
              <div>
                <div className="font-display font-bold text-[#0a2b52] leading-tight">
                  Save up to 60%
                </div>
                <div className="text-xs text-slate-500">on your premiums</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="max-w-7xl mx-auto px-6 -mt-6 relative z-10">
        <div className="glass-strong rounded-3xl grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200/60">
          {stats.map((s) => (
            <div key={s.label} className="px-6 py-8 text-center">
              <div className="font-display text-3xl md:text-4xl font-extrabold brand-gradient-text">
                {s.value}
              </div>
              <div className="text-sm text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="max-w-7xl mx-auto px-6 pt-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
            What we offer
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[#0a2b52] mt-2">
            Coverage for everything that matters
          </h2>
          <p className="text-slate-600 mt-4">
            From your car and home to your business and travels — we've got you
            protected.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon] || ShieldCheck;
            return (
              <Link
                to={`/${s.slug}`}
                key={s.slug}
                className="group glass rounded-3xl overflow-hidden card-hover fade-up"
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a2b52]/60 to-transparent" />
                  <div className="absolute top-4 left-4 h-11 w-11 rounded-xl glass-strong flex items-center justify-center text-[#0066b3]">
                    <Icon size={22} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-[#0a2b52]">
                    {s.title}
                  </h3>
                  <p className="text-slate-600 text-sm mt-2 leading-relaxed line-clamp-3">
                    {s.intro}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[#0066b3] font-semibold text-sm group-hover:gap-3 transition-all">
                    Learn more <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="max-w-7xl mx-auto px-6 pt-24">
        <div className="glass-strong rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="aurora bg-[#cfe4f6] h-64 w-64 -bottom-20 -right-10" />
          <div className="relative grid lg:grid-cols-3 gap-10 items-center">
            <div>
              <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
                Why Cloverdale
              </span>
              <h2 className="font-display text-3xl font-extrabold text-[#0a2b52] mt-2">
                An insurance agency you can trust
              </h2>
              <p className="text-slate-600 mt-4">
                We are committed to providing financial security for you and
                your family, making sure your protection has the right features
                when you need it most.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              {whyUs.map((w) => {
                const Icon = iconMap[w.icon] || ShieldCheck;
                return (
                  <div
                    key={w.title}
                    className="bg-white/70 rounded-2xl p-5 card-hover border border-white/60"
                  >
                    <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0066b3] to-[#003c7a] flex items-center justify-center text-white mb-3">
                      <Icon size={22} />
                    </div>
                    <h4 className="font-display font-bold text-[#0a2b52]">
                      {w.title}
                    </h4>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                      {w.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== ICBC RENEWAL BAND ===== */}
      <section className="max-w-7xl mx-auto px-6 pt-24">
        <div className="rounded-3xl bg-gradient-to-br from-[#0066b3] to-[#003c7a] p-8 md:p-12 relative overflow-hidden">
          <div className="aurora bg-[#8ec4ec] h-64 w-64 -top-16 right-0 opacity-30" />
          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center gap-2 bg-white/15 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <RefreshCw size={15} /> Fast & Easy
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white">
                Renew your ICBC Autoplan online
              </h2>
              <p className="text-white/85 mt-3 max-w-xl">
                Start your ICBC policy renewal in minutes, or let our Surrey team
                handle it for you by phone and email — 7 days a week.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Link
                to="/icbc-policy-renewal"
                className="bg-white text-[#003c7a] px-7 py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#eef6fd] transition-colors"
              >
                <RefreshCw size={18} /> Renew ICBC Online
              </Link>
              <a
                href={`tel:${company.phoneRaw}`}
                className="border border-white/60 text-white px-7 py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
              >
                <Phone size={18} /> {company.phone}
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* ===== PARTNERS LOGOS ===== */}
      <section className="pt-24">
        <div className="max-w-7xl mx-auto px-6 text-center mb-8">
          <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
            Trusted Insurer Partners
          </span>
          <h2 className="font-display text-3xl font-extrabold text-[#0a2b52] mt-2">
            We work with Canada's leading insurers
          </h2>
          <p className="text-slate-600 mt-3">
            Click any partner below to visit their official website.
          </p>
        </div>
        <PartnersCarousel />
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="max-w-7xl mx-auto px-6 pt-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
            Client Stories
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[#0a2b52] mt-2">
            Loved by families across the Lower Mainland
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {testimonials.map((t) => (
            <div key={t.name} className="glass rounded-3xl p-7 card-hover">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed">"{t.quote}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#0066b3] to-[#003c7a] flex items-center justify-center text-white font-bold">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-[#0a2b52]">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CTA + QUOTE FORM ===== */}
      <section className="max-w-7xl mx-auto px-6 pt-24">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
              Get Started
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[#0a2b52] mt-2">
              Request your free, no-obligation quote today
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Fill out the form and one of our friendly Surrey-based agents will
              reach out with the best coverage options and rates for your needs.
            </p>
            <div className="mt-8 space-y-4">
              {company.hours.map((h) => (
                <div key={h.day} className="flex items-center gap-3">
                  <Clock size={18} className="text-[#0066b3]" />
                  <span className="text-[#0a2b52] font-medium">{h.day}:</span>
                  <span className="text-slate-500">{h.time}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-strong rounded-3xl p-7 md:p-9">
            <h3 className="font-display text-2xl font-bold text-[#0a2b52] mb-6">
              Free Quote Request
            </h3>
            <QuoteForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
